/**
 * 车辆运行时：把链路上的帧归集成"界面可以直接读"的状态快照，并把事件同步派发出去。
 *
 * 三条设计线，后面所有管理器（告警、参数、指令、任务、校准）都建立在这三条之上：
 *
 * 1. **状态是快照，事件是通知。** `state` 永远整块替换（`Object.freeze` 过的只读对象），
 *    而不是就地改字段。界面上的面板来自不同频率的消息（姿态 10Hz、电池 0.5Hz），
 *    "哪些字段这一帧变了、界面该重画哪一块"是一个只有上帝视角才能回答的问题；
 *    整块快照把这个问题的答案变成"引用变了就重算"，订阅者自己也就能决定要不要重渲染。
 *
 * 2. **`null` 与 `0` 是两件事。** 字段有三种状态：没收到过、收到但值不可用
 *    （MAVLink 用哨兵值表示，例如 `battery_remaining = -1`）、收到且值确实是 0。
 *    三者对界面意味着完全不同的东西（"没有数据" / "数据不可用" / "真的是 0"），
 *    所以一律用 `null` 表示前两者，并且**每个子系统各自记录最后更新时刻**：
 *    姿态的合理失联时长是 1 秒级、电池是 10 秒级、返航点是分钟级，
 *    一个全局时间戳无法同时表达这三者。
 *
 * 3. **同步派发，中间不经过任何异步层。** 一帧进来 → 更新状态 → 同一个调用栈里通知订阅者。
 *    这样"读到最新状态"和"收到通知"之间不存在窗口；细节见 events.ts 的说明。
 *    这个文件**不 import React、不持有任何 DOM 引用**：运行时必须能放进 Worker，
 *    也必须能在没有渲染环境的 node 里被完整单测。
 *
 * 反向的规则同样重要：**发送必须让出当前调用栈**（见 `yieldToCallStack`）。
 * 事件是同步的，订阅者很自然地会在回调里直接回一条消息；若那条消息在同一个栈里写出去，
 * 就会在解析器解析到一半时再灌一次数据，已经交付的帧会被重复交付。
 * 上层管理器（任务、参数）都在事件回调里发送，所以这条规则放在 `Vehicle.sendCommandLong()`
 * 里统一执行，而不是要求每个调用点自己记得。
 */
import {
  mavTypeName,
  modeDisplayName,
  vehicleKindOf,
  type VehicleKind,
} from '@hgc/ardupilot';
import {
  MavlinkSession,
  type LinkState,
  type SessionOptions,
  type VehiclePresence,
} from '@hgc/link';
import {
  decodePayload,
  enumByName,
  enumValue,
  messageById,
  messageByName,
  type DecodedValues,
  type MavlinkFrame,
  type MessageDefinition,
  type UnknownMessageInfo,
} from '@hgc/mavlink';

import { createEventBus, type EventBus, type Unsubscribe } from './events';

// ---------------------------------------------------------------------------
// 协议约定值
// ---------------------------------------------------------------------------

/** `MAV_COMP_ID_AUTOPILOT1`：飞控的标准组件号 */
export const MAV_COMP_ID_AUTOPILOT1 = 1;

/** `MAV_AUTOPILOT_INVALID`：组件用它自报"我不是自动驾驶仪"（伴飞计算机、云台、相机） */
const MAV_AUTOPILOT_INVALID = 8;

/** `MAV_AUTOPILOT_GENERIC`：没说是哪种自动驾驶仪 —— 也不能当成飞控 */
const MAV_AUTOPILOT_GENERIC = 0;

/** `MAV_TYPE_GCS`：另一台地面站也在链路上时会出现，它显然不是机体 */
const MAV_TYPE_GCS = 6;

/** `MAV_CMD_DO_SET_HOME`：设置返航点（权威表在 @hgc/ardupilot，这里只用到这一个） */
const MAV_CMD_DO_SET_HOME = 179;

/** `MAV_CMD_REQUEST_MESSAGE`：请对端重发指定消息 */
const MAV_CMD_REQUEST_MESSAGE = 512;

/** `UINT8_MAX` / `UINT16_MAX`：字段宽度决定的"无数据"哨兵值 */
const UINT8_MAX = 0xff;
const UINT16_MAX = 0xffff;

/**
 * 自动请求返航点之间的最小间隔。
 *
 * 有些固件不支持 `MAV_CMD_REQUEST_MESSAGE`（会回 UNSUPPORTED），如果每次都重试，
 * 就会在链路上留下一条每帧一次的"请求—不支持"噪声，把真正有用的遥测挤掉。
 * 这里只做节流，不做重试。
 */
const HOME_REQUEST_THROTTLE_MS = 5000;

/**
 * 保留的最近状态文本条数。
 *
 * 100 条够覆盖一次完整的开机自检播报（PreArm 系列失败通常十几条），
 * 再多就只是让"最近状态文本"面板没人看得完，并且每条快照都要复制一遍整个列表。
 */
export const MAX_STATUSTEXT_ENTRIES = 100;

// ---------------------------------------------------------------------------
// 枚举表查询（懒建 + 缓存：协议表的索引按需构建，见 descriptor.ts）
// ---------------------------------------------------------------------------

let armedFlagValue: number | null = null;

/**
 * `MAV_MODE_FLAG_SAFETY_ARMED`。从生成的枚举表取，不硬编码数字 ——
 * 但表与代码不同步时（生成物损坏）要退回标准值：把"是否已经解锁"这个
 * 最关键的判断变成 `NaN` 造成的静默错误，比一个偏差 0 的常数危险得多。
 */
function armedModeFlag(): number {
  armedFlagValue ??= enumValue('MAV_MODE_FLAG', 'MAV_MODE_FLAG_SAFETY_ARMED') ?? 128;
  return armedFlagValue;
}

/**
 * 界面按它选面板与单位（多旋翼看电机/姿态，固定翼看空速/爬升率）。
 *
 * 它与 `@hgc/ardupilot` 的 `VehicleKind` **不是**同一件事，职责不同：
 * - `VehicleKind` 回答"该用哪一张模式表、哪一套领域知识"，一个 ArduPilot 固件一个值
 *   （copter / plane / rover / sub / tracker / blimp）。
 * - `VehicleClass` 回答"界面上给哪一块面板"，所以它多一个 `'other'`
 *   （天线跟踪器、飞艇这类没有专属面板的机型都落在它上面），少两个
 *   `tracker` / `blimp`。**不要**因为看着像就把它换成 `VehicleKind`：
 *   面板划分与固件划分不是一回事，合并之后"没有专属面板"这个状态就表达不出来了。
 *
 * 机型判断本身仍然是**领域知识**，只由 `@hgc/ardupilot` 的 `vehicles.ts` 决定
 * （`MAV_TYPE` 生成表驱动）。这里只做一次纯粹的"领域类别 → 界面分桶"折叠，
 * 见 {@link VEHICLE_CLASS_BY_KIND}。本文件曾经自己抄了一份 `MAV_TYPE` 小表，
 * 结果是同一架飞机在两个包里可能归成不同的类 —— 那类分歧从任何单侧代码都看不出来。
 */
export type VehicleClass = 'copter' | 'plane' | 'rover' | 'sub' | 'other' | 'unknown';

/**
 * 领域类别 → 界面分桶。
 *
 * 唯一一处映射，别处不要再自己判断机型。写成 `Record<VehicleKind, …>` 而不是
 * 一串 if：领域层将来新增一类（上游加了新固件、或者 blimp 被正式纳入）时，
 * 这里会直接编译不过，而不是默默落进 `'other'` 让面板选错。
 */
const VEHICLE_CLASS_BY_KIND: Readonly<Record<VehicleKind, VehicleClass>> = {
  copter: 'copter',
  plane: 'plane',
  rover: 'rover',
  sub: 'sub',
  // 天线跟踪器与飞艇有完整的领域知识（模式表、单位），但界面还没有专属面板
  tracker: 'other',
  blimp: 'other',
  unknown: 'unknown',
};

/**
 * EKF 是否健康。
 *
 * 位值全部来自生成的 `EKF_STATUS_FLAGS` 表 —— 位域写错的表现是"健康状态永远是错的"，
 * 而那会直接变成"解锁被拒"，排查方向会指向与原因完全无关的地方。
 *
 * 判定口径：姿态 + 水平位置 + 垂直方向的**任一**估计量，
 * 并且没有 `EKF_UNINITIALIZED` / `EKF_GPS_GLITCHING`。
 * 垂直方向不要求 `POS_VERT_ABS`：只认它会把"没有气压计但有 GPS 垂直速度"的机体
 * 一律判成 EKF 故障，而这类机体的姿态与位置估计是好的。
 * 误报比漏报更能摧毁操作员对告警的信任 —— 一个天天喊"EKF 故障"的地面站会被无视。
 */
function isEkfHealthy(flags: number): boolean {
  const bit = (name: string): number => enumValue('EKF_STATUS_FLAGS', name) ?? 0;
  const uninitialized = bit('EKF_UNINITIALIZED');
  const glitching = bit('EKF_GPS_GLITCHING');
  if (uninitialized !== 0 && (flags & uninitialized) !== 0) return false;
  if (glitching !== 0 && (flags & glitching) !== 0) return false;

  const attitude = (flags & bit('EKF_ATTITUDE')) !== 0;
  const horizontal = (flags & (bit('EKF_POS_HORIZ_ABS') | bit('EKF_POS_HORIZ_REL'))) !== 0;
  const verticalBits = bit('EKF_POS_VERT_ABS') | bit('EKF_POS_VERT_AGL') | bit('EKF_VELOCITY_VERT');
  const vertical = verticalBits !== 0 && (flags & verticalBits) !== 0;
  return attitude && horizontal && vertical;
}

let severityNames: Map<number, string> | null = null;

/** `MAV_SEVERITY` 数值 → 枚举名。告警分级只认枚举名，不认数字。 */
function severityName(value: number): string | null {
  if (severityNames === null) {
    severityNames = new Map();
    for (const entry of enumByName('MAV_SEVERITY')?.entries ?? []) severityNames.set(entry.value, entry.name);
  }
  return severityNames.get(value) ?? null;
}

/** `MAV_FRAME` 里以 `MAV_FRAME_GLOBAL` 开头的都是"带全局坐标"的框架（从枚举名推，不硬编码数字） */
function isGlobalFrame(frame: number): boolean {
  const entries = enumByName('MAV_FRAME')?.entries ?? [];
  for (const entry of entries) {
    if (entry.value === frame) return entry.name.startsWith('MAV_FRAME_GLOBAL');
  }
  return false;
}

/** `HOME_POSITION` 的消息 ID（从协议表取；取不到就说明生成物有问题，此时不发请求） */
function homePositionMessageId(): number | null {
  return messageByName('HOME_POSITION')?.id ?? null;
}

// ---------------------------------------------------------------------------
// 字段读取：解码结果是 `FieldValue` 的联合类型，读之前必须先收窄
// ---------------------------------------------------------------------------

function numberField(values: DecodedValues, name: string): number | null {
  const value = values[name];
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // 64 位整数解出来是 bigint；能安全转成 number 就转，不能就当作"读不到"
  if (typeof value === 'bigint') {
    const converted = Number(value);
    return Number.isSafeInteger(converted) ? converted : null;
  }
  return null;
}

function stringField(values: DecodedValues, name: string): string | null {
  const value = values[name];
  return typeof value === 'string' ? value : null;
}

/** 读数组字段里的某个元素（例如 `BATTERY_STATUS.voltages[0]`） */
function numberAt(values: DecodedValues, name: string, index: number): number | null {
  const value = values[name];
  if (typeof value !== 'object' || value === null || !Array.isArray(value)) return null;
  const item: unknown = value[index];
  return typeof item === 'number' && Number.isFinite(item) ? item : null;
}

const RAD_TO_DEG = 180 / Math.PI;

/**
 * 让出当前调用栈（一个微任务），保证发送不会发生在"正在处理某一帧"的同步栈里。
 *
 * 背景：链路会话的 `transport.write` 在有些通道上是**同步回灌**的（内置模拟机就是这样，
 * 某些网关/回环通道也是），对端会在同一个调用栈里把回应帧写回来，
 * 于是解析器会在帧回调中途被再次 `push`。
 *
 * 解析器现在**已经能正确处理这种重入**（见 `MavlinkParser` 的 `draining` 标志：
 * 重入的字节只会追加到缓冲区尾部，由外层循环继续消费，不会重复交付）。
 * 所以这里的让出**不再是正确性的前提**，保留它是为了另外两件事：
 *   1. 不让帧派发被发送动作拖长 —— 解析循环里做 I/O 会让一次 push 的耗时变得不可预测；
 *   2. 少一层对"接收方实现是否可重入"的隐式依赖。
 * 换句话说：删掉它现在也不会出错，但如果哪天解析器的重入保护被改动，
 * 这一层仍是最后的缓冲。**不要**因为"解析器已经安全了"就把它当成冗余删掉。
 */
function yieldToCallStack(): Promise<void> {
  return Promise.resolve();
}

// ---------------------------------------------------------------------------
// 状态快照
// ---------------------------------------------------------------------------

/**
 * 需要单独记录"最后更新时刻"的子系统。
 *
 * 按子系统分开而不是一个全局时间戳：位置丢 2 秒就要提示，电池丢 2 秒毫无意义
 * （它本来就是 0.5Hz），返航点几乎不变。一个全局值只能表达三者中最悲观的那个，
 * 结果是界面要么到处闪"过期"，要么该报的时候不报。
 */
export type TelemetryKey =
  | 'heartbeat'
  | 'attitude'
  | 'position'
  | 'gps'
  | 'battery'
  | 'ekf'
  | 'vibration'
  | 'rc'
  | 'hud'
  | 'home'
  | 'mission'
  | 'statustext';

/**
 * 各子系统的"过期"阈值（毫秒），供 `isTelemetryStale()` 使用。
 *
 * 数值来自消息的**名义频率**再放宽若干倍，而不是拍脑袋：
 * 阈值太紧会让正常抖动被显示成故障，太松就等于没有过期判断。
 */
export const TELEMETRY_STALE_MS: Readonly<Record<TelemetryKey, number>> = Object.freeze({
  /** 与链路的心跳判定同源：心跳超时就是断链，这里没有第二种解释 */
  heartbeat: 3000,
  /** 名义 10Hz；允许连丢若干帧才算过期，避免正常的调度抖动就闪黄 */
  attitude: 1000,
  /** 名义 2Hz（ArduPilot 默认 `SRx_POSITION`） */
  position: 2000,
  /** 名义 1Hz，但 GPS 本身就会偶发迟到 */
  gps: 5000,
  /** 名义 0.5Hz → 2 秒；电池变化慢，阈值给宽，否则它永远显示"过期" */
  battery: 15000,
  ekf: 5000,
  vibration: 5000,
  rc: 3000,
  hud: 3000,
  /** 返航点只有设置/解锁时才更新，用秒级的阈值判断它没有意义 */
  home: 60000,
  /**
   * `MISSION_CURRENT` 上游的要求是"一直推"（名义 1Hz），但它的**值**只在切航点时变，
   * 所以阈值给得比 1Hz 宽得多：容忍对端降频，也容忍"只在事件时才发"的实现。
   */
  mission: 10000,
  /** 状态文本也是事件驱动；最后一条在几分钟内都还值得留在界面上 */
  statustext: 300000,
});

/** 一条状态文本。原文照收，不做翻译与截断 —— 本地化是界面层的事。 */
export interface StatustextEntry {
  /** `MAV_SEVERITY` 原始值 */
  readonly severity: number;
  /** `MAV_SEVERITY` 的枚举名（例如 `MAV_SEVERITY_WARNING`）；查不到时为 null */
  readonly severityName: string | null;
  /** 原文（中文/英文都原样保留） */
  readonly text: string;
  readonly at: number;
  readonly systemId: number;
  readonly componentId: number;
  /** 多帧拼接时的分片序号；未分片为 0，拼接后是最后一片的序号 */
  readonly chunkSeq: number;
}

/** 振动样本（来自 `VIBRATION`）。EKF_STATUS_REPORT 里没有振动量，只有协方差。 */
export interface VibrationSample {
  readonly x: number | null;
  readonly y: number | null;
  readonly z: number | null;
  /** 三个加速度计的累计裁剪次数之和：ArduPilot 判断"振动打到量程"看的就是它 */
  readonly clipping: number | null;
  readonly at: number;
}

/**
 * 车辆状态快照：只读、不可变、整块替换。
 *
 * 所有字段的 `null` 都表示"没有可用数据"（没收到过，或收到但值是哨兵值），
 * 与"收到且为 0"严格区分。
 */
export interface VehicleState {
  // --- 身份与连接 ---
  readonly systemId: number;
  readonly componentId: number;
  /** 会话的心跳判定结果（本类不另做一套超时逻辑） */
  readonly online: boolean;
  readonly lastHeartbeatAt: number | null;
  readonly armed: boolean;
  /** `MAV_STATE` 原始值 */
  readonly systemStatus: number | null;
  /** 链路状态。会话级：同一条链路上的所有车辆共享同一个值 */
  readonly linkState: LinkState;

  // --- 心跳 ---
  readonly type: number | null;
  /** `MAV_TYPE` 的枚举名 */
  readonly typeName: string | null;
  readonly vehicleClass: VehicleClass;
  /** `MAV_AUTOPILOT` 原始值 */
  readonly autopilot: number | null;
  readonly baseMode: number | null;
  /**
   * ArduPilot 把 `packet.custom_mode` 原样交给 `set_mode()`，
   * 所以它**就是**模式编号本身，不是位域。
   */
  readonly customMode: number | null;
  /**
   * 模式的可读名字，**中文模式名**（`LOITER` → `悬停`）。
   *
   * 编号 → 名字的权威表在 `@hgc/ardupilot`：模式号来自 MAVLink 生成产物，
   * 中文标注在那里手写，所以上游增删模式后这里会自动跟上。
   *
   * 认不出来的模式号降级成 `#编号`（例如 `#42`）。这一条刻意不做成"抛错"或
   * "UNKNOWN"：HEARTBEAT 是几十赫兹的广播，抛错会把整条遥测链路炸掉；
   * 而 `UNKNOWN` 会让人以为存在一个叫这个名字的模式。`#42` 则一眼就能看出
   * "这个号我们没见过"，顺带把原始编号留在界面上供排查（上游新增模式、
   * 或者是非 ArduPilot 固件在报自己的编号）。
   */
  readonly modeName: string | null;
  /** 这个组件是不是飞控（组件号 1，或心跳自报了一个真正的自动驾驶仪类型）；未知为 null */
  readonly isAutopilot: boolean | null;

  // --- 姿态（ATTITUDE 是弧度，这里统一换成度） ---
  readonly rollDeg: number | null;
  readonly pitchDeg: number | null;
  readonly yawDeg: number | null;

  // --- 位置（GLOBAL_POSITION_INT） ---
  readonly latDeg: number | null;
  readonly lonDeg: number | null;
  /** 海拔（MSL） */
  readonly altM: number | null;
  /** 相对起飞点 */
  readonly relativeAltM: number | null;
  /** 罗盘航向；`hdg` 为 `UINT16_MAX` 时是 null（注意 0 是有效的正北） */
  readonly headingDeg: number | null;
  readonly vxMps: number | null;
  readonly vyMps: number | null;
  readonly vzMps: number | null;

  // --- GPS（GPS_RAW_INT） ---
  readonly fixType: number | null;
  readonly fixTypeName: string | null;
  readonly satellites: number | null;
  readonly hdop: number | null;
  readonly vdop: number | null;

  // --- 电池 ---
  readonly voltageV: number | null;
  readonly currentA: number | null;
  /** 剩余电量百分比；没收到过或对端报 -1 时是 null（**不是 0**） */
  readonly remainingPct: number | null;

  // --- 估计器（EKF_STATUS_REPORT / VIBRATION） ---
  readonly ekfFlags: number | null;
  readonly ekfHealthy: boolean | null;
  readonly ekfVelocityVariance: number | null;
  readonly ekfPosHorizVariance: number | null;
  readonly ekfPosVertVariance: number | null;
  readonly vibration: VibrationSample | null;

  // --- 遥控 ---
  /** 每个元素是原始 PWM（µs）；通道无数据时为 null，整个数组在没收到时是 null */
  readonly rcChannels: readonly (number | null)[] | null;
  /** `UINT8_MAX`（255）表示无信号，当作 null */
  readonly rssi: number | null;

  // --- 飞行（VFR_HUD） ---
  readonly airspeedMps: number | null;
  readonly groundspeedMps: number | null;
  readonly throttlePct: number | null;
  readonly climbRateMps: number | null;

  // --- 返航点（HOME_POSITION） ---
  readonly homeLatDeg: number | null;
  readonly homeLonDeg: number | null;
  readonly homeAltM: number | null;

  // --- 任务进度 ---
  readonly missionCurrentSeq: number | null;
  readonly missionTotal: number | null;
  readonly missionReached: number | null;

  // --- 最近的状态文本（新 → 旧，最多 MAX_STATUSTEXT_ENTRIES 条） ---
  readonly statustext: readonly StatustextEntry[];

  /** 各子系统的最后更新时刻：判断"这一块数据是不是已经过期"的唯一依据 */
  readonly updatedAt: Readonly<Record<TelemetryKey, number | null>>;
}

// ---------------------------------------------------------------------------
// 事件
// ---------------------------------------------------------------------------

export interface CommandAckEvent {
  readonly command: number;
  /** `MAV_RESULT` */
  readonly result: number;
  /** 0–100 的进度；MAVLink 用 `UINT8_MAX` 表示"没有进度信息"，此时省略该属性 */
  readonly progress?: number;
  /** 第二个结果参数（`MAV_CMD_DO_SET_HOME` 之类用它回显） */
  readonly resultParam2: number;
  readonly at: number;
}

export interface ParamValueEvent {
  readonly name: string;
  readonly value: number;
  readonly index: number;
  readonly count: number;
  /** `MAV_PARAM_TYPE` */
  readonly type: number;
  readonly at: number;
}

/**
 * 一条航线项。
 *
 * `MISSION_ITEM`（float）与 `MISSION_ITEM_INT`（degE7）在线上是两条不同的消息，
 * 上层必须能分清（混起来会让纬度精度从厘米退化到米级），所以这里带 `int` 标记；
 * `latDeg` / `lonDeg` 只在全局框架下才有意义，本地框架（NED）下是 null ——
 * 无条件把 x/y 当经纬度会让所有"本地框架"的航线项出现在几内亚湾。
 */
export interface MissionItemEvent {
  readonly seq: number;
  readonly command: number;
  /** `MAV_FRAME` 原始值 */
  readonly frameRaw: number;
  readonly current: boolean;
  readonly autocontinue: boolean;
  readonly param1: number;
  readonly param2: number;
  readonly param3: number;
  readonly param4: number;
  readonly latDeg: number | null;
  readonly lonDeg: number | null;
  readonly altM: number | null;
  readonly missionType: number;
  /** 是否来自 `MISSION_ITEM_INT` */
  readonly int: boolean;
  readonly at: number;
}

export interface MissionCountEvent {
  readonly count: number;
  readonly missionType: number;
  readonly at: number;
}

/** 机体要求我们发第 `seq` 条航线项（上传流程靠它推进） */
export interface MissionRequestEvent {
  readonly seq: number;
  readonly missionType: number;
  /** 对端要的是 INT 版本还是 float 版本 */
  readonly int: boolean;
  readonly at: number;
}

export interface MissionAckEvent {
  /** `MAV_MISSION_RESULT` */
  readonly type: number;
  readonly missionType: number;
  readonly opaqueId: number;
  readonly at: number;
}

export interface MissionItemReachedEvent {
  readonly seq: number;
  readonly at: number;
}

export interface LinkStateEvent {
  readonly state: LinkState;
  readonly detail?: string;
}

/**
 * 车辆事件表。
 *
 * `state` 与 `frame` 是两个极端：前者每次变化都发（子节点自己决定要不要重渲染），
 * 后者是原始帧、订阅者可能为零（检查器 / 遥测记录才用）。两者都必须是同步的。
 */
export type VehicleEvents = {
  /** 状态快照发生变化。每次变化都发，而不是只在"重要字段"变化时才发 */
  state: VehicleState;
  /** 每一帧原始帧 */
  frame: MavlinkFrame;
  statustext: StatustextEntry;
  commandAck: CommandAckEvent;
  paramValue: ParamValueEvent;
  missionItem: MissionItemEvent;
  missionRequest: MissionRequestEvent;
  missionCount: MissionCountEvent;
  missionAck: MissionAckEvent;
  missionItemReached: MissionItemReachedEvent;
  /** 链路状态变化（会话级，同一条链路上的每辆车都会发一遍） */
  linkState: LinkStateEvent;
};

// ---------------------------------------------------------------------------
// 数据来源
// ---------------------------------------------------------------------------

/**
 * 车辆的数据来源。
 *
 * 为什么不是"直接给 `MavlinkSession` 挂 onFrame"：会话只接受**一个** `onFrame` 回调
 * （它是构造参数，不是订阅），而一条链路上可能有多辆车、还要同时喂给遥测记录器。
 * 所以由上层（注册表或 `SessionFeed`）统一接收，再按 `systemId:componentId` 分发；
 * 换成文件回放通道时，改的只是这个接口的实现。
 */
export interface VehicleFeed {
  /** 上行帧的来源；发送命令也走这个会话 */
  readonly session: MavlinkSession;
  subscribeFrames(listener: (frame: MavlinkFrame) => void): Unsubscribe;
  subscribeLinkState(listener: (change: LinkStateEvent) => void): Unsubscribe;
  subscribePresence(listener: (vehicles: readonly VehiclePresence[]) => void): Unsubscribe;
  subscribeUnknownMessages(listener: (info: UnknownMessageInfo) => void): Unsubscribe;
  /** 当前链路上已知的机体/组件（会话的在线判定结果） */
  presence(): readonly VehiclePresence[];
}

/** 各来源的订阅者数量，用于泄漏检查 */
export interface FeedListenerCount {
  readonly frames: number;
  readonly linkState: number;
  readonly presence: number;
  readonly unknownMessages: number;
}

/**
 * 把 `MavlinkSession` 包装成可多路分发的 `VehicleFeed`。
 *
 * 调用方自己传的 `onFrame` / `onStateChange` / `onPresenceChange` / `onUnknownMessage`
 * 会被**保留**（在分发之后调用），因此遥测记录器之类的消费者可以直接挂在会话上。
 */
export class SessionFeed implements VehicleFeed {
  readonly session: MavlinkSession;

  private readonly frameListeners = new Set<(frame: MavlinkFrame) => void>();
  private readonly linkStateListeners = new Set<(change: LinkStateEvent) => void>();
  private readonly presenceListeners = new Set<(vehicles: readonly VehiclePresence[]) => void>();
  private readonly unknownListeners = new Set<(info: UnknownMessageInfo) => void>();

  constructor(options: SessionOptions) {
    this.session = new MavlinkSession({
      ...options,
      onFrame: (frame) => {
        // 先快照再派发：订阅者在回调里退订/新订是常见写法（与事件总线同理）
        for (const listener of [...this.frameListeners]) listener(frame);
        options.onFrame?.(frame);
      },
      onStateChange: (state, detail) => {
        const change: LinkStateEvent = detail === undefined ? { state } : { state, detail };
        for (const listener of [...this.linkStateListeners]) listener(change);
        options.onStateChange?.(state, detail);
      },
      onPresenceChange: (vehicles) => {
        for (const listener of [...this.presenceListeners]) listener(vehicles);
        options.onPresenceChange?.(vehicles);
      },
      onUnknownMessage: (info) => {
        for (const listener of [...this.unknownListeners]) listener(info);
        options.onUnknownMessage?.(info);
      },
    });
  }

  subscribeFrames(listener: (frame: MavlinkFrame) => void): Unsubscribe {
    this.frameListeners.add(listener);
    return () => this.frameListeners.delete(listener);
  }

  subscribeLinkState(listener: (change: LinkStateEvent) => void): Unsubscribe {
    this.linkStateListeners.add(listener);
    return () => this.linkStateListeners.delete(listener);
  }

  subscribePresence(listener: (vehicles: readonly VehiclePresence[]) => void): Unsubscribe {
    this.presenceListeners.add(listener);
    return () => this.presenceListeners.delete(listener);
  }

  subscribeUnknownMessages(listener: (info: UnknownMessageInfo) => void): Unsubscribe {
    this.unknownListeners.add(listener);
    return () => this.unknownListeners.delete(listener);
  }

  presence(): readonly VehiclePresence[] {
    return this.session.vehicles();
  }

  /** 与事件总线的 `listenerCount()` 同理：订阅忘记取消时的表现是"越跑越卡"，没有别的线索 */
  listenerCount(): FeedListenerCount {
    return {
      frames: this.frameListeners.size,
      linkState: this.linkStateListeners.size,
      presence: this.presenceListeners.size,
      unknownMessages: this.unknownListeners.size,
    };
  }
}

// ---------------------------------------------------------------------------
// 运行统计
// ---------------------------------------------------------------------------

export interface VehicleFrameError {
  readonly kind: 'decode' | 'handler';
  readonly message: string;
  readonly messageId: number;
  readonly reason: string;
  readonly at: number;
}

export interface VehicleStats {
  /** 会话交付给本车的帧数（协议表里没有的消息不会走这条路，它们进 `unknownMessages`） */
  readonly frames: number;
  /** 载荷解码失败（长度不足等）的帧数 */
  readonly decodeErrors: number;
  /** 归集逻辑自身抛错的帧数（有的话是缺陷，但也不许影响后续帧） */
  readonly handlerErrors: number;
  /** 按消息名统计的失败次数 */
  readonly errorsByMessage: Readonly<Record<string, number>>;
  /** 协议表里有、但本类暂不归集的消息 → 次数（排障用：确认"这帧被看到了，只是没处理"） */
  readonly unsupported: Readonly<Record<string, number>>;
  /** 协议表里没有的消息 ID → 次数（固件比地面站新时会出现） */
  readonly unknownMessages: Readonly<Record<number, number>>;
  /** 最近一次失败，供诊断面板直接显示 */
  readonly lastError: VehicleFrameError | null;
}

// ---------------------------------------------------------------------------
// Vehicle
// ---------------------------------------------------------------------------

export interface VehicleOptions {
  readonly systemId: number;
  readonly componentId: number;
  readonly feed: VehicleFeed;
  /** 时钟；用于事件时间戳与节流判断。默认 `Date.now` */
  readonly now?: () => number;
  /** 事件监听器抛错的出口。默认静默（理由见 events.ts） */
  readonly onListenerError?: (error: unknown, type: string) => void;
}

/** 可变版本的快照，只在类内部使用；对外永远是冻结过的 `VehicleState` */
interface MutableState {
  online: boolean;
  lastHeartbeatAt: number | null;
  armed: boolean;
  systemStatus: number | null;
  linkState: LinkState;
  type: number | null;
  typeName: string | null;
  vehicleClass: VehicleClass;
  autopilot: number | null;
  baseMode: number | null;
  customMode: number | null;
  modeName: string | null;
  isAutopilot: boolean | null;
  rollDeg: number | null;
  pitchDeg: number | null;
  yawDeg: number | null;
  latDeg: number | null;
  lonDeg: number | null;
  altM: number | null;
  relativeAltM: number | null;
  headingDeg: number | null;
  vxMps: number | null;
  vyMps: number | null;
  vzMps: number | null;
  fixType: number | null;
  fixTypeName: string | null;
  satellites: number | null;
  hdop: number | null;
  vdop: number | null;
  voltageV: number | null;
  currentA: number | null;
  remainingPct: number | null;
  ekfFlags: number | null;
  ekfHealthy: boolean | null;
  ekfVelocityVariance: number | null;
  ekfPosHorizVariance: number | null;
  ekfPosVertVariance: number | null;
  vibration: VibrationSample | null;
  rcChannels: readonly (number | null)[] | null;
  rssi: number | null;
  airspeedMps: number | null;
  groundspeedMps: number | null;
  throttlePct: number | null;
  climbRateMps: number | null;
  homeLatDeg: number | null;
  homeLonDeg: number | null;
  homeAltM: number | null;
  missionCurrentSeq: number | null;
  missionTotal: number | null;
  missionReached: number | null;
  /** 环形缓冲：拼接分片时会被就地修改，因此内部存的是可变版本，出快照时才复制 + 冻结 */
  statustext: MutableStatustext[];
  updatedAt: Record<TelemetryKey, number | null>;
}

interface MutableStatustext {
  severity: number;
  severityName: string | null;
  text: string;
  at: number;
  systemId: number;
  componentId: number;
  chunkSeq: number;
}

/**
 * 一辆车（准确地说：链路上的一个 `systemId:componentId`）。
 *
 * 同一系统号的飞控与伴飞计算机是两个组件、两辆车 —— 它们发的是不同的消息流，
 * 合成一个状态会让"电压"与"姿态"来自不同进程而没人看得出来。
 */
export class Vehicle {
  /** `${systemId}:${componentId}`，与链路会话的 `VehiclePresence.key` 一致 */
  readonly id: string;
  readonly systemId: number;
  readonly componentId: number;
  readonly events: EventBus<VehicleEvents>;

  private readonly feed: VehicleFeed;
  private readonly now: () => number;
  private readonly mutable: MutableState;
  private readonly unsubscribes: Unsubscribe[] = [];

  private snapshotValue: VehicleState | null = null;
  /** 快照是否已经落后于内部状态（零订阅者时不急着构造） */
  private dirty = true;
  private disposed = false;

  private homeRequestedAt: number | null = null;

  private frames = 0;
  private decodeErrors = 0;
  private handlerErrors = 0;
  private readonly errorsByMessage = new Map<string, number>();
  private readonly unsupportedMessages = new Map<string, number>();
  private readonly unknownMessages = new Map<number, number>();
  private lastError: VehicleFrameError | null = null;

  constructor(options: VehicleOptions) {
    this.systemId = options.systemId;
    this.componentId = options.componentId;
    this.id = `${options.systemId}:${options.componentId}`;
    this.feed = options.feed;
    this.now = options.now ?? (() => Date.now());
    this.events = createEventBus<VehicleEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );

    this.mutable = {
      online: this.lookupOnline(this.feed.presence()),
      lastHeartbeatAt: null,
      armed: false,
      systemStatus: null,
      linkState: this.feed.session.state,
      type: null,
      typeName: null,
      vehicleClass: 'unknown',
      autopilot: null,
      baseMode: null,
      customMode: null,
      modeName: null,
      // 还没收到心跳时，组件号 1 是飞控的标准身份，其余组件在自报之前是"未知"
      isAutopilot: options.componentId === MAV_COMP_ID_AUTOPILOT1 ? true : null,
      rollDeg: null,
      pitchDeg: null,
      yawDeg: null,
      latDeg: null,
      lonDeg: null,
      altM: null,
      relativeAltM: null,
      headingDeg: null,
      vxMps: null,
      vyMps: null,
      vzMps: null,
      fixType: null,
      fixTypeName: null,
      satellites: null,
      hdop: null,
      vdop: null,
      voltageV: null,
      currentA: null,
      remainingPct: null,
      ekfFlags: null,
      ekfHealthy: null,
      ekfVelocityVariance: null,
      ekfPosHorizVariance: null,
      ekfPosVertVariance: null,
      vibration: null,
      rcChannels: null,
      rssi: null,
      airspeedMps: null,
      groundspeedMps: null,
      throttlePct: null,
      climbRateMps: null,
      homeLatDeg: null,
      homeLonDeg: null,
      homeAltM: null,
      missionCurrentSeq: null,
      missionTotal: null,
      missionReached: null,
      statustext: [],
      updatedAt: {
        heartbeat: null,
        attitude: null,
        position: null,
        gps: null,
        battery: null,
        ekf: null,
        vibration: null,
        rc: null,
        hud: null,
        home: null,
        mission: null,
        statustext: null,
      },
    };

    this.unsubscribes.push(this.feed.subscribeFrames((frame) => this.handleFrame(frame)));
    this.unsubscribes.push(
      this.feed.subscribeLinkState((change) => this.handleLinkState(change)),
    );
    this.unsubscribes.push(this.feed.subscribePresence(() => this.handlePresence()));
    this.unsubscribes.push(
      this.feed.subscribeUnknownMessages((info) => {
        if (info.systemId !== this.systemId || info.componentId !== this.componentId) return;
        this.unknownMessages.set(info.messageId, (this.unknownMessages.get(info.messageId) ?? 0) + 1);
      }),
    );
  }

  /** 当前状态快照（只读、冻结、引用稳定：没变化时返回同一个对象） */
  get state(): VehicleState {
    if (this.snapshotValue === null || this.dirty) return this.materialize();
    return this.snapshotValue;
  }

  get session(): MavlinkSession {
    return this.feed.session;
  }

  /** 这个组件是不是飞控；还没收到心跳且组件号不是 1 时为 null（未知） */
  get isAutopilot(): boolean | null {
    return this.mutable.isAutopilot;
  }

  /** 是否已销毁。销毁后帧不再被归集 */
  get isDisposed(): boolean {
    return this.disposed;
  }

  /**
   * 归集一帧。
   *
   * 由 feed 驱动（车辆自己订阅了 feed 的帧流）；回放通道（.tlog）也可以直接调用它
   * 把历史帧"重演"成状态。注册表会把**整条链路**的帧广播给每一辆车，所以这里必须
   * 只认自己的 —— 否则两辆车的姿态会互相覆盖，而表现是"数值偶尔跳一下"，几乎无法定位。
   */
  handleFrame(frame: MavlinkFrame): void {
    if (this.disposed) return;
    if (frame.systemId !== this.systemId || frame.componentId !== this.componentId) return;

    this.frames += 1;

    const definition = messageById(frame.messageId);
    if (definition === undefined) {
      // 正常情况下解析器不会把协议表里没有的消息送到这里，走到这里说明订阅被接错了
      this.unknownMessages.set(frame.messageId, (this.unknownMessages.get(frame.messageId) ?? 0) + 1);
      return;
    }

    let values: DecodedValues;
    try {
      values = decodePayload(definition, frame.payload);
    } catch (error) {
      /*
       * 载荷长度不足等：计入计数后**继续处理下一帧**。
       * 一帧解不开就让整条链路停摆，等于把一次数据问题升级成一次飞行事故。
       */
      this.recordError('decode', definition, frame, error);
      this.events.emit('frame', frame);
      return;
    }

    let changed: boolean | null = null;
    try {
      changed = this.applyMessage(definition.name, values, frame);
    } catch (error) {
      this.recordError('handler', definition, frame, error);
      this.events.emit('frame', frame);
      return;
    }

    if (changed === null) {
      // 协议表里有但不归集的消息也要记数：排障时要能区分"没收到"和"收到了但没处理"
      this.unsupportedMessages.set(
        definition.name,
        (this.unsupportedMessages.get(definition.name) ?? 0) + 1,
      );
    } else if (changed) {
      this.publish();
    }

    /*
     * 原始帧在状态更新**之后**发。
     *
     * 反过来的话，"记下这一帧、顺手读一下 state"的订阅者会读到上一帧的状态 ——
     * 这种错位只在偶发的时序里显现，且看起来像"界面慢了一拍"，几乎无法复现。
     *
     * 性能：帧事件是逐帧发的，但 `emit` 在零订阅者时是一次 Map 查找就直接返回，
     * 而且发出去的就是解析器已经造好的那个帧对象（不为它派生任何东西）。
     * 所以"没人看"的时候，这条路径的开销就是那一次查找。
     */
    this.events.emit('frame', frame);
  }

  /**
   * 请求机体重发返航点。
   *
   * `HOME_POSITION` 默认不推流，必须先请求；没有它，界面上的"回家"标记与
   * 返航距离这些读数就一直是空的。返回 `false` 表示被节流挡住、链路没打开、
   * 或协议表里查不到消息 ID —— 都不是错误。
   *
   * 节流只在**发送成功**时打点：链路还没打开时不该算作"已经请求过"，
   * 否则开机瞬间的那次失败会让返航点永远空着。
   */
  async requestHome(): Promise<boolean> {
    await yieldToCallStack();
    if (this.disposed) return false;
    const at = this.now();
    if (this.homeRequestedAt !== null && at - this.homeRequestedAt < HOME_REQUEST_THROTTLE_MS) {
      return false;
    }
    const messageId = homePositionMessageId();
    if (messageId === null) return false;
    try {
      const sent = await this.feed.session.sendCommandLong(MAV_CMD_REQUEST_MESSAGE, [messageId], {
        systemId: this.systemId,
        componentId: this.componentId,
      });
      if (sent) this.homeRequestedAt = at;
      return sent;
    } catch {
      // 发送失败（链路刚断）不是异常情况：下一次心跳会再触发一次
      return false;
    }
  }

  /**
   * 向本车发一条 `COMMAND_LONG`（自动填目标系统/组件，避免每个管理器各写一遍）。
   *
   * 发送前先让出当前调用栈，理由见 `yieldToCallStack` —— 上层管理器几乎都是
   * 在事件回调里发送（收到 `missionRequest` 就回一条航线项），如果不让出，
   * 帧处理与发送会互相嵌套。
   */
  async sendCommandLong(
    command: number,
    params: readonly (number | undefined)[] = [],
  ): Promise<boolean> {
    await yieldToCallStack();
    if (this.disposed) return false;
    return this.feed.session.sendCommandLong(command, params, {
      systemId: this.systemId,
      componentId: this.componentId,
    });
  }

  stats(): VehicleStats {
    return Object.freeze({
      frames: this.frames,
      decodeErrors: this.decodeErrors,
      handlerErrors: this.handlerErrors,
      errorsByMessage: Object.freeze(Object.fromEntries(this.errorsByMessage)),
      unsupported: Object.freeze(Object.fromEntries(this.unsupportedMessages)),
      unknownMessages: Object.freeze(
        Object.fromEntries([...this.unknownMessages].map(([id, count]) => [String(id), count])),
      ),
      lastError: this.lastError,
    }) as VehicleStats;
  }

  /**
   * 取消全部订阅。
   *
   * 车辆**不**负责关闭会话：一条链路上的会话可能被多辆车共享，
   * 一辆车"消失"（例如伴飞计算机重启）不该把整条链路关掉。
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const off of this.unsubscribes) off();
    this.unsubscribes.length = 0;
    this.events.clear();
  }

  // -------------------------------------------------------------------------
  // 内部：状态发布
  // -------------------------------------------------------------------------

  /**
   * 状态有变化 → 通知订阅者。
   *
   * 没有订阅者时只置脏标记、不构造快照：遥测每秒几十帧，
   * 为一个没人看的对象付分配 + 冻结的成本没有意义（订阅者后来一读，`state` 会现算）。
   */
  private publish(): void {
    if (this.events.listenerCount('state') > 0) {
      this.events.emit('state', this.materialize());
      return;
    }
    this.dirty = true;
  }

  private materialize(): VehicleState {
    // 在线状态每次现算：会话是唯一判定方，本类不再实现第二套超时逻辑
    this.mutable.online = this.lookupOnline(this.feed.presence());
    const m = this.mutable;
    const snapshot: VehicleState = Object.freeze({
      systemId: this.systemId,
      componentId: this.componentId,
      online: m.online,
      lastHeartbeatAt: m.lastHeartbeatAt,
      armed: m.armed,
      systemStatus: m.systemStatus,
      linkState: m.linkState,
      type: m.type,
      typeName: m.typeName,
      vehicleClass: m.vehicleClass,
      autopilot: m.autopilot,
      baseMode: m.baseMode,
      customMode: m.customMode,
      modeName: m.modeName,
      isAutopilot: m.isAutopilot,
      rollDeg: m.rollDeg,
      pitchDeg: m.pitchDeg,
      yawDeg: m.yawDeg,
      latDeg: m.latDeg,
      lonDeg: m.lonDeg,
      altM: m.altM,
      relativeAltM: m.relativeAltM,
      headingDeg: m.headingDeg,
      vxMps: m.vxMps,
      vyMps: m.vyMps,
      vzMps: m.vzMps,
      fixType: m.fixType,
      fixTypeName: m.fixTypeName,
      satellites: m.satellites,
      hdop: m.hdop,
      vdop: m.vdop,
      voltageV: m.voltageV,
      currentA: m.currentA,
      remainingPct: m.remainingPct,
      ekfFlags: m.ekfFlags,
      ekfHealthy: m.ekfHealthy,
      ekfVelocityVariance: m.ekfVelocityVariance,
      ekfPosHorizVariance: m.ekfPosHorizVariance,
      ekfPosVertVariance: m.ekfPosVertVariance,
      vibration: m.vibration,
      rcChannels: m.rcChannels,
      rssi: m.rssi,
      airspeedMps: m.airspeedMps,
      groundspeedMps: m.groundspeedMps,
      throttlePct: m.throttlePct,
      climbRateMps: m.climbRateMps,
      homeLatDeg: m.homeLatDeg,
      homeLonDeg: m.homeLonDeg,
      homeAltM: m.homeAltM,
      missionCurrentSeq: m.missionCurrentSeq,
      missionTotal: m.missionTotal,
      missionReached: m.missionReached,
      // 状态文本是环形缓冲（会被就地修改），复制一份再交出去；
      // 反转成"新 → 旧"：面板要在顶部显示最新一条，而环形缓冲是按时间顺序追加的
      statustext: Object.freeze([...m.statustext].reverse()),
      updatedAt: Object.freeze({ ...m.updatedAt }),
    });
    this.snapshotValue = snapshot;
    this.dirty = false;
    return snapshot;
  }

  private handleLinkState(change: LinkStateEvent): void {
    if (this.disposed) return;
    this.mutable.linkState = change.state;
    this.events.emit('linkState', change);
    // 链路状态在快照里，所以它变化也算状态变化
    this.publish();
  }

  private handlePresence(): void {
    if (this.disposed) return;
    const online = this.lookupOnline(this.feed.presence());
    if (online === this.mutable.online) return;
    // 先写进内部状态，再发布：否则 materialize() 会在"现算"时算出同一个值，
    // 但又因为 dirty 而多发一次完全相同的快照
    this.mutable.online = online;
    this.publish();
  }

  private lookupOnline(presence: readonly VehiclePresence[]): boolean {
    for (const record of presence) {
      if (record.key === this.id) return record.online;
    }
    return false;
  }

  private recordError(
    kind: 'decode' | 'handler',
    definition: MessageDefinition,
    frame: MavlinkFrame,
    error: unknown,
  ): void {
    if (kind === 'decode') this.decodeErrors += 1;
    else this.handlerErrors += 1;
    this.errorsByMessage.set(
      definition.name,
      (this.errorsByMessage.get(definition.name) ?? 0) + 1,
    );
    this.lastError = {
      kind,
      message: definition.name,
      messageId: definition.id,
      reason: error instanceof Error ? error.message : String(error),
      at: frame.receivedAt,
    };
  }

  // -------------------------------------------------------------------------
  // 内部：按消息归集
  // -------------------------------------------------------------------------

  /**
   * @returns `null` = 协议表里有但本类不归集（要计数）；
   *          `true` = 归集且快照变了（要通知订阅者）；
   *          `false` = 归集但快照没变 —— 例如参数下载那成千上万条 `PARAM_VALUE`、
   *          以及航线传输中的航线项。它们各自的事件照发，但"状态快照变了"是假的，
   *          发出去只会让界面在做一次参数下载时被重渲染上千次。
   */
  private applyMessage(name: string, values: DecodedValues, frame: MavlinkFrame): boolean | null {
    const at = frame.receivedAt;
    const m = this.mutable;

    switch (name) {
      case 'HEARTBEAT': {
        const wasArmed = m.armed;
        const type = numberField(values, 'type');
        const autopilot = numberField(values, 'autopilot');
        const baseMode = numberField(values, 'base_mode');
        const customMode = numberField(values, 'custom_mode');

        m.type = type;
        m.typeName = type === null ? null : (mavTypeName(type) ?? null);
        /*
         * 机型与模式名都从领域层问出来：`MAV_TYPE` → 类别的判断、类别 → 模式表，
         * 两个映射都只有那一份权威实现。这里只把领域类别折成界面分桶。
         *
         * `type === null`（这条心跳没带 type 字段）时只能取 `'unknown'`：
         * `vehicleKindOf` 要一个数字，而"字段缺失"与"值没有映射"是两件事。
         */
        const kind: VehicleKind = type === null ? 'unknown' : vehicleKindOf(type);
        m.vehicleClass = VEHICLE_CLASS_BY_KIND[kind];
        m.autopilot = autopilot;
        m.baseMode = baseMode;
        m.customMode = customMode;
        /*
         * 模式名要按**机型**查表：同一个数字在 Copter 与 Plane 上是两个模式
         * （5 = Copter 的 LOITER，Plane 的 5 是 FLY_BY_WIRE_A），
         * 所以必须在算出 kind 之后再查，不能用一个全局模式表。
         * 注意用的是 `kind` 而不是 `vehicleClass`：飞艇与天线跟踪器的分桶是 'other'，
         * 但它们的模式表是完整可用的，按 'other' 查会白白退化成 `#编号`。
         */
        m.modeName = customMode === null ? null : modeDisplayName(kind, customMode);
        m.armed = baseMode !== null && (baseMode & armedModeFlag()) !== 0;
        m.systemStatus = numberField(values, 'system_status');
        m.isAutopilot = isAutopilotHeartbeat(this.componentId, autopilot, type);
        m.lastHeartbeatAt = at;
        m.updatedAt.heartbeat = at;

        // 连接后的第一件事就是拿到返航点（它默认不推流）；解锁是第二个时机。
        // 只在这两个时机自动请求，不做周期性重试 —— 对面不支持时重试只是链路噪声。
        if (m.homeLatDeg === null && (m.updatedAt.home === null || (!wasArmed && m.armed))) {
          void this.requestHome();
        }
        return true;
      }

      case 'SYS_STATUS': {
        const voltage = numberField(values, 'voltage_battery');
        // 0 与 UINT16_MAX 都是"没有电池信息"。当成 0V 会让界面报"电池 0.0V"这种
        // 不存在的紧急情况，而操作员会为此中断飞行
        m.voltageV = voltage === null || voltage === 0 || voltage === UINT16_MAX ? null : voltage / 1000;
        const current = numberField(values, 'current_battery');
        // 电流是 cA（10mA）；-1 是"不可用"的约定值
        m.currentA = current === null || current < 0 ? null : current / 100;
        m.updatedAt.battery = at;
        return true;
      }

      case 'BATTERY_STATUS': {
        const remaining = numberField(values, 'battery_remaining');
        // int8 的 -1 是"不可用"，必须和"0%（真的没电了）"分开：
        // 把不可用显示成 0% 会让操作员以为电池已经空了
        m.remainingPct = remaining === null || remaining < 0 ? null : remaining;
        // 电压/电流以 SYS_STATUS 为准（系统级汇总）。这里只兜底，
        // 因为两条消息不是同一时刻采样的，同时写会让界面上的电压来回跳
        if (m.voltageV === null) {
          const pack = numberAt(values, 'voltages', 0);
          if (pack !== null && pack !== 0 && pack !== UINT16_MAX) m.voltageV = pack / 1000;
        }
        if (m.currentA === null) {
          const current = numberField(values, 'current_battery');
          if (current !== null && current >= 0) m.currentA = current / 100;
        }
        m.updatedAt.battery = at;
        return true;
      }

      case 'ATTITUDE': {
        const roll = numberField(values, 'roll');
        const pitch = numberField(values, 'pitch');
        const yaw = numberField(values, 'yaw');
        m.rollDeg = roll === null ? null : roll * RAD_TO_DEG;
        m.pitchDeg = pitch === null ? null : pitch * RAD_TO_DEG;
        m.yawDeg = yaw === null ? null : yaw * RAD_TO_DEG;
        m.updatedAt.attitude = at;
        return true;
      }

      case 'GLOBAL_POSITION_INT': {
        const lat = numberField(values, 'lat');
        const lon = numberField(values, 'lon');
        /*
         * 经纬度是 int32 的 degE7，必须除以 1e7 才能用。
         * (0, 0) 是几内亚湾外海：**没有定位时飞控发的就是全 0**，
         * 把它当有效坐标会把机体画到非洲西岸 —— 比显示"无位置"糟得多。
         */
        const hasFix = lat !== null && lon !== null && !(lat === 0 && lon === 0);
        m.latDeg = hasFix ? lat / 1e7 : null;
        m.lonDeg = hasFix ? lon / 1e7 : null;

        const alt = numberField(values, 'alt');
        m.altM = alt === null ? null : alt / 1000;
        const relativeAlt = numberField(values, 'relative_alt');
        m.relativeAltM = relativeAlt === null ? null : relativeAlt / 1000;

        const hdg = numberField(values, 'hdg');
        // hdg 是 cdeg，UINT16_MAX 表示"航向未知"。0 是**有效的正北**，两者不能混
        m.headingDeg = hdg === null || hdg === UINT16_MAX || hdg < 0 ? null : hdg / 100;

        const vx = numberField(values, 'vx');
        const vy = numberField(values, 'vy');
        const vz = numberField(values, 'vz');
        m.vxMps = vx === null ? null : vx / 100;
        m.vyMps = vy === null ? null : vy / 100;
        m.vzMps = vz === null ? null : vz / 100;
        m.updatedAt.position = at;
        return true;
      }

      case 'GPS_RAW_INT': {
        const fix = numberField(values, 'fix_type');
        m.fixType = fix;
        m.fixTypeName = fix === null ? null : fixTypeName(fix);
        m.satellites = numberField(values, 'satellites_visible');
        const eph = numberField(values, 'eph');
        const epv = numberField(values, 'epv');
        // eph/epv 是 HDOP/VDOP ×100，UINT16_MAX 表示未知
        m.hdop = eph === null || eph === UINT16_MAX ? null : eph / 100;
        m.vdop = epv === null || epv === UINT16_MAX ? null : epv / 100;
        m.updatedAt.gps = at;
        return true;
      }

      case 'VFR_HUD': {
        m.airspeedMps = numberField(values, 'airspeed');
        m.groundspeedMps = numberField(values, 'groundspeed');
        m.throttlePct = numberField(values, 'throttle');
        m.climbRateMps = numberField(values, 'climb');
        // 高度只在没有 GLOBAL_POSITION_INT 时兜底：VFR_HUD 的 alt 是米级 float，
        // 两者混用会让高度在两条消息之间来回抖
        if (m.altM === null) m.altM = numberField(values, 'alt');
        m.updatedAt.hud = at;
        return true;
      }

      case 'EKF_STATUS_REPORT': {
        const flags = numberField(values, 'flags');
        m.ekfFlags = flags;
        m.ekfHealthy = flags === null ? null : isEkfHealthy(flags);
        m.ekfVelocityVariance = numberField(values, 'velocity_variance');
        m.ekfPosHorizVariance = numberField(values, 'pos_horiz_variance');
        m.ekfPosVertVariance = numberField(values, 'pos_vert_variance');
        m.updatedAt.ekf = at;
        return true;
      }

      case 'VIBRATION': {
        const x = numberField(values, 'vibration_x');
        const y = numberField(values, 'vibration_y');
        const z = numberField(values, 'vibration_z');
        const clipping0 = numberField(values, 'clipping_0');
        const clipping1 = numberField(values, 'clipping_1');
        const clipping2 = numberField(values, 'clipping_2');
        const clipping =
          clipping0 === null || clipping1 === null || clipping2 === null
            ? null
            : clipping0 + clipping1 + clipping2;
        m.vibration = Object.freeze({ x, y, z, clipping, at });
        m.updatedAt.vibration = at;
        return true;
      }

      case 'RC_CHANNELS': {
        const count = numberField(values, 'chancount');
        const total = count === null ? 0 : Math.min(Math.max(Math.trunc(count), 0), 18);
        const channels: (number | null)[] = [];
        for (let index = 1; index <= total; index += 1) {
          const raw = numberField(values, `chan${index}_raw`);
          /*
           * 0 与 UINT16_MAX 都表示"这个通道没有数据"。
           * 0 µs 不是任何舵机能接受的脉宽，把它当有效值会让界面显示通道在极限位置，
           * 而操作员会去查一个根本不存在的故障。
           */
          channels.push(raw === null || raw === 0 || raw === UINT16_MAX ? null : raw);
        }
        m.rcChannels = total === 0 ? null : Object.freeze(channels);
        const rssi = numberField(values, 'rssi');
        // rssi 是 uint8，哨兵是 UINT8_MAX（255）而不是 UINT16_MAX —— 字段宽度决定哨兵值
        m.rssi = rssi === null || rssi === UINT8_MAX ? null : rssi;
        m.updatedAt.rc = at;
        return true;
      }

      case 'HOME_POSITION': {
        const lat = numberField(values, 'latitude');
        const lon = numberField(values, 'longitude');
        const hasFix = lat !== null && lon !== null && !(lat === 0 && lon === 0);
        m.homeLatDeg = hasFix ? lat / 1e7 : null;
        m.homeLonDeg = hasFix ? lon / 1e7 : null;
        const altitude = numberField(values, 'altitude');
        m.homeAltM = altitude === null ? null : altitude / 1000;
        m.updatedAt.home = at;
        return true;
      }

      case 'STATUSTEXT': {
        const severity = numberField(values, 'severity') ?? 0;
        const chunkSeq = numberField(values, 'chunk_seq') ?? 0;
        const text = stringField(values, 'text') ?? '';
        const entry = this.pushStatustext(text, severity, chunkSeq, frame);
        m.updatedAt.statustext = at;
        this.events.emit('statustext', entry);
        return true;
      }

      case 'COMMAND_ACK': {
        const command = numberField(values, 'command') ?? 0;
        const result = numberField(values, 'result') ?? 0;
        const progress = numberField(values, 'progress');
        const resultParam2 = numberField(values, 'result_param2') ?? 0;
        const event: CommandAckEvent = {
          command,
          result,
          // 255 = UINT8_MAX = "没有进度信息"：带上它会让进度条从 0 跳到 100
          ...(progress === null || progress === UINT8_MAX ? {} : { progress }),
          resultParam2,
          at,
        };
        this.events.emit('commandAck', event);

        // 返航点被改过：本地缓存的坐标已经不对了，清掉并重新请求
        const homeMoved = command === MAV_CMD_DO_SET_HOME && result === 0;
        if (homeMoved) {
          m.homeLatDeg = null;
          m.homeLonDeg = null;
          m.homeAltM = null;
          m.updatedAt.home = null;
          this.homeRequestedAt = null;
          void this.requestHome();
        }
        return homeMoved;
      }

      case 'PARAM_VALUE': {
        const paramName = stringField(values, 'param_id');
        const value = numberField(values, 'param_value');
        // 名字或数值读不出来时这一条没有意义（参数管理器也没法用它），静默跳过即可
        if (paramName === null || value === null) return false;
        this.events.emit('paramValue', {
          name: paramName,
          value,
          index: numberField(values, 'param_index') ?? 0,
          count: numberField(values, 'param_count') ?? 0,
          type: numberField(values, 'param_type') ?? 0,
          at,
        });
        return false;
      }

      case 'MISSION_COUNT': {
        const count = numberField(values, 'count') ?? 0;
        // MISSION_COUNT.count 是基础字段，0 就是"没有航点"，可以直接采信
        m.missionTotal = count;
        m.updatedAt.mission = at;
        this.events.emit('missionCount', {
          count,
          missionType: numberField(values, 'mission_type') ?? 0,
          at,
        });
        return true;
      }

      case 'MISSION_ITEM':
      case 'MISSION_ITEM_INT': {
        this.events.emit('missionItem', missionItemEvent(name === 'MISSION_ITEM_INT', values, at));
        // 航线项不算进度：下载时它会成千上万条地来，每一条都发状态事件没有意义
        return false;
      }

      case 'MISSION_REQUEST':
      case 'MISSION_REQUEST_INT': {
        this.events.emit('missionRequest', {
          seq: numberField(values, 'seq') ?? 0,
          missionType: numberField(values, 'mission_type') ?? 0,
          int: name === 'MISSION_REQUEST_INT',
          at,
        });
        return false;
      }

      case 'MISSION_ACK': {
        this.events.emit('missionAck', {
          type: numberField(values, 'type') ?? 0,
          missionType: numberField(values, 'mission_type') ?? 0,
          opaqueId: numberField(values, 'opaque_id') ?? 0,
          at,
        });
        return false;
      }

      case 'MISSION_CURRENT': {
        m.missionCurrentSeq = numberField(values, 'seq');
        const total = numberField(values, 'total');
        /*
         * `total` 是 MAVLink 2 扩展字段，而扩展字段会被"尾部零字节截断"影响：
         * 读到的 0 既可能是"真的没有航点"，也可能是"对端这条消息根本没带这个字段"。
         * 总数 0 对界面没有用处，而把"其实有 5 个航点"写成 0 会让整条进度显示全错，
         * 所以这里只接受正数。
         */
        if (total !== null && total > 0 && total !== UINT16_MAX) m.missionTotal = total;
        m.updatedAt.mission = at;
        return true;
      }

      case 'MISSION_ITEM_REACHED': {
        const seq = numberField(values, 'seq') ?? 0;
        m.missionReached = seq;
        m.updatedAt.mission = at;
        this.events.emit('missionItemReached', { seq, at });
        return true;
      }

      default:
        return null;
    }
  }

  private pushStatustext(
    text: string,
    severity: number,
    chunkSeq: number,
    frame: MavlinkFrame,
  ): StatustextEntry {
    const previous = this.mutable.statustext[this.mutable.statustext.length - 1];
    /*
     * `STATUSTEXT.text` 只有 50 字节，长句子（PreArm 系列）会被拆成多帧，
     * 靠 `chunk_seq` 排序。这里只做"序号接得上就拼接"这一档最小处理：
     * 接不上就当作新的一条 —— 宁可多出一条，也不要靠猜把两条不相干的消息粘在一起，
     * 粘错的消息比截断的消息更容易误导人。
     */
    if (
      chunkSeq > 0 &&
      previous !== undefined &&
      previous.chunkSeq === chunkSeq - 1 &&
      previous.systemId === frame.systemId &&
      previous.componentId === frame.componentId
    ) {
      previous.text += text;
      previous.chunkSeq = chunkSeq;
      previous.at = frame.receivedAt;
      return Object.freeze({ ...previous });
    }

    const entry: MutableStatustext = {
      severity,
      severityName: severityName(severity),
      text,
      at: frame.receivedAt,
      systemId: frame.systemId,
      componentId: frame.componentId,
      chunkSeq,
    };
    this.mutable.statustext.push(entry);
    const overflow = this.mutable.statustext.length - MAX_STATUSTEXT_ENTRIES;
    if (overflow > 0) this.mutable.statustext.splice(0, overflow);
    return Object.freeze({ ...entry });
  }
}

// ---------------------------------------------------------------------------
// 归集辅助
// ---------------------------------------------------------------------------

function isAutopilotHeartbeat(
  componentId: number,
  autopilot: number | null,
  type: number | null,
): boolean {
  // 组件号 1（MAV_COMP_ID_AUTOPILOT1）是飞控的标准身份
  if (componentId === MAV_COMP_ID_AUTOPILOT1) return true;
  // 其余组件只有在自报了一个真正的自动驾驶仪类型时才算飞控；
  // MAV_AUTOPILOT_INVALID 正是伴飞计算机/云台/相机用来自报"我不是飞控"的值
  if (autopilot === null) return false;
  if (autopilot === MAV_AUTOPILOT_INVALID || autopilot === MAV_AUTOPILOT_GENERIC) return false;
  return type !== MAV_TYPE_GCS;
}

let fixTypeNames: Map<number, string> | null = null;

function fixTypeName(value: number): string | null {
  if (fixTypeNames === null) {
    fixTypeNames = new Map();
    for (const entry of enumByName('GPS_FIX_TYPE')?.entries ?? []) fixTypeNames.set(entry.value, entry.name);
  }
  return fixTypeNames.get(value) ?? null;
}

function missionItemEvent(int: boolean, values: DecodedValues, at: number): MissionItemEvent {
  const frameRaw = numberField(values, 'frame') ?? 0;
  const x = numberField(values, 'x');
  const y = numberField(values, 'y');
  // 只有全局框架下 x/y 才是坐标：本地框架（NED）下它们是相对原点的米，
  // 无条件当经纬度会让这些航线项出现在几内亚湾
  const global = isGlobalFrame(frameRaw);
  const scale = int ? 1e7 : 1;
  return {
    seq: numberField(values, 'seq') ?? 0,
    command: numberField(values, 'command') ?? 0,
    frameRaw,
    current: (numberField(values, 'current') ?? 0) > 0,
    autocontinue: (numberField(values, 'autocontinue') ?? 0) > 0,
    param1: numberField(values, 'param1') ?? 0,
    param2: numberField(values, 'param2') ?? 0,
    param3: numberField(values, 'param3') ?? 0,
    param4: numberField(values, 'param4') ?? 0,
    latDeg: global && x !== null ? x / scale : null,
    lonDeg: global && y !== null ? y / scale : null,
    // z 在 INT 与 float 两个版本里都是 float 米
    altM: numberField(values, 'z'),
    missionType: numberField(values, 'mission_type') ?? 0,
    int,
    at,
  };
}

// ---------------------------------------------------------------------------
// 新鲜度判断（纯函数：界面可以只依赖快照，不必再问车辆）
// ---------------------------------------------------------------------------

/** 某个子系统距上次更新的时长（毫秒）；从未收到过返回 null */
export function telemetryAgeMs(state: VehicleState, key: TelemetryKey, now: number): number | null {
  const updatedAt = state.updatedAt[key];
  return updatedAt === null ? null : Math.max(0, now - updatedAt);
}

/**
 * 某个子系统的数据是不是已经过期。
 *
 * 从未收到过时返回 `true`（"没有数据"和"很久没更新"对界面是同一件事：
 * 不能拿它当有效读数），这与字段是 `null` 的处理方式一致。
 */
export function isTelemetryStale(
  state: VehicleState,
  key: TelemetryKey,
  now: number,
  maxAgeMs: number = TELEMETRY_STALE_MS[key],
): boolean {
  const age = telemetryAgeMs(state, key, now);
  return age === null || age > maxAgeMs;
}

// ---------------------------------------------------------------------------
// VehicleRegistry
// ---------------------------------------------------------------------------

export interface VehicleAddedEvent {
  readonly vehicleId: string;
  readonly vehicle: Vehicle;
  readonly at: number;
}

export interface VehicleRemovedEvent {
  readonly vehicleId: string;
  readonly at: number;
}

export interface VehicleStateEvent {
  readonly vehicleId: string;
  readonly vehicle: Vehicle;
  readonly state: VehicleState;
  /** 是不是当前操作对象（界面据此决定谁画在地图中心） */
  readonly primary: boolean;
}

export interface PrimaryChangedEvent {
  readonly vehicleId: string | null;
  readonly vehicle: Vehicle | null;
  readonly at: number;
}

export type VehicleRegistryEvents = {
  /** 一个此前没出现过的 `systemId:componentId` 第一次发了帧 */
  added: VehicleAddedEvent;
  removed: VehicleRemovedEvent;
  /** 任一车辆的状态变化 */
  state: VehicleStateEvent;
  /** 当前操作对象的状态变化：绝大多数界面只需要订阅这一个 */
  primaryState: VehicleState;
  primaryChanged: PrimaryChangedEvent;
  /** 链路状态变化（会话级） */
  linkState: LinkStateEvent;
};

export interface VehicleRegistryOptions {
  /**
   * 会话与链路配置。
   *
   * `onFrame` / `onStateChange` / `onPresenceChange` / `onUnknownMessage` 会被注册表
   * 接过去做多路分发；调用方自己传的话**不会**被覆盖，而是在分发之后被调用
   * （遥测记录器可以直接挂在这里）。
   */
  readonly session: SessionOptions;
  /** 事件监听器抛错的出口。默认静默 */
  readonly onListenerError?: (error: unknown, type: string) => void;
}

/**
 * 多机注册表：按 `systemId:componentId` 维护链路上的每一辆车。
 *
 * **同一系统号出现多个组件号是常态**（飞控是 comp 1，伴飞计算机、云台、相机各占一个号），
 * 因此这里的策略是：
 *
 *   - 每个 `systemId:componentId` 都是一辆车：伴飞计算机的状态文本与飞控的遥测
 *     来自不同进程，混进一个状态就再也分不开"这条消息是谁说的"；
 *   - `isAutopilot` 用组件号 1 加心跳里的 `MAV_AUTOPILOT` 自报值判定；
 *   - 焦点（primary）优先给飞控：在用户没手动选过之前，若当前对象不是飞控、
 *     而新出现的是飞控，就自动切过去。**绝不在两架飞机的飞控之间自动跳焦点**
 *     —— 一架飞机在天上时焦点自己跑了，是比"没自动切换"严重得多的事故；
 *   - 用户显式 `setPrimary()` 之后，自动切换彻底关闭，直到 `setPrimary(null)`。
 *
 * 注册表只转发 `state` 与 `linkState`：状态文本、命令确认、参数、任务这些**逐条**的事件
 * 留在各辆车的总线上。需要它们的管理器按 `added` 事件订阅对应那辆车
 * （`registry.events.on('added', ({ vehicle }) => vehicle.events.on('statustext', ...))`），
 * 这样"谁发的"这个信息不会在转发过程中被抹掉。
 */
export class VehicleRegistry {
  readonly events: EventBus<VehicleRegistryEvents>;
  readonly feed: SessionFeed;

  private readonly vehicles = new Map<string, Vehicle>();
  /** 每辆车的转发订阅，随车辆一起清理 */
  private readonly forwarders = new Map<string, Unsubscribe[]>();
  private readonly unsubscribes: Unsubscribe[] = [];
  private readonly now: () => number;
  private currentPrimaryId: string | null = null;
  /** 焦点是用户显式指定的：此后自动策略不再改动它 */
  private primaryExplicit = false;
  private disposed = false;

  constructor(options: VehicleRegistryOptions) {
    this.now = options.session.now ?? (() => Date.now());
    this.feed = new SessionFeed(options.session);
    this.events = createEventBus<VehicleRegistryEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );

    this.unsubscribes.push(
      this.feed.subscribeFrames((frame) => this.routeFrame(frame)),
      this.feed.subscribeLinkState((change) => this.events.emit('linkState', change)),
    );
  }

  get session(): MavlinkSession {
    return this.feed.session;
  }

  get primaryId(): string | null {
    return this.currentPrimaryId;
  }

  get primary(): Vehicle | null {
    if (this.currentPrimaryId === null) return null;
    return this.vehicles.get(this.currentPrimaryId) ?? null;
  }

  /** 当前链路上的所有车辆（按 `systemId`、`componentId` 排序，顺序稳定 —— 界面列表不该每帧重排） */
  list(): readonly Vehicle[] {
    return [...this.vehicles.values()].sort(
      (a, b) => a.systemId - b.systemId || a.componentId - b.componentId,
    );
  }

  /** 同一系统号下的全部组件（飞控 + 伴飞计算机等），界面据此把它们归到一架飞机下 */
  listBySystem(systemId: number): readonly Vehicle[] {
    return this.list().filter((vehicle) => vehicle.systemId === systemId);
  }

  get(id: string): Vehicle | undefined {
    return this.vehicles.get(id);
  }

  /**
   * 指定当前操作对象。
   *
   * `null` 表示"交还给自动策略"：立刻按"飞控优先"重新选一个，而不是把界面留在一个
   * 没有焦点的状态 —— 后者在链路上没有新帧时会一直持续下去。
   * ID 不存在时返回 `false` 而不是抛错：界面上的 ID 可能来自上一帧的快照。
   */
  setPrimary(id: string | null): boolean {
    if (id === null) {
      this.primaryExplicit = false;
      this.switchPrimary(this.pickFallbackPrimary()?.id ?? null, false);
      return true;
    }
    if (!this.vehicles.has(id)) return false;
    this.switchPrimary(id, true);
    return true;
  }

  async open(): Promise<void> {
    await this.session.open();
  }

  /** 关闭链路，但保留已归集的状态（重连后 `list()` 仍然是同一批车辆） */
  async close(): Promise<void> {
    await this.session.close();
  }

  /** 移除一辆车：取消它的全部订阅并销毁它。用于显式释放（例如用户关掉某个伴飞计算机） */
  remove(id: string): boolean {
    const vehicle = this.vehicles.get(id);
    if (vehicle === undefined) return false;
    this.vehicles.delete(id);

    for (const off of this.forwarders.get(id) ?? []) off();
    this.forwarders.delete(id);

    vehicle.dispose();
    if (this.currentPrimaryId === id) {
      const fallback = this.pickFallbackPrimary();
      this.switchPrimary(fallback?.id ?? null, false);
    }
    this.events.emit('removed', { vehicleId: id, at: this.now() });
    return true;
  }

  /**
   * 彻底销毁：取消全部订阅、关闭会话、销毁每辆车、清空事件总线。
   *
   * 订阅没清干净的后果是"跑得越久越卡"，而不是任何一处报错 ——
   * 所以这里把每一个 subscribe 都记在册，并在最后断言式地清空。
   */
  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;

    for (const off of this.unsubscribes) off();
    this.unsubscribes.length = 0;

    await this.feed.session.close();

    for (const id of [...this.vehicles.keys()]) this.remove(id);
    this.currentPrimaryId = null;
    this.primaryExplicit = false;
    this.events.clear();
  }

  // -------------------------------------------------------------------------
  // 内部：路由
  // -------------------------------------------------------------------------

  private routeFrame(frame: MavlinkFrame): void {
    if (this.disposed) return;
    const key = `${frame.systemId}:${frame.componentId}`;
    if (this.vehicles.has(key)) return;

    // 任何一帧都能确认"这个组件存在"：等心跳会把伴飞计算机这类不发心跳的组件漏掉，
    // 而多机支持正是靠"新身份出现就自动建车"这件事成立的
    const vehicle = this.createVehicle(frame.systemId, frame.componentId);
    this.events.emit('added', { vehicleId: vehicle.id, vehicle, at: this.now() });
    this.maybeUpgradePrimary(vehicle);
    /*
     * 车辆自己订阅帧流（这样单独拿出来也能用），但**新订阅赶不上正在派发的这一帧**：
     * 派发用的是订阅者快照。创建它的那一帧必须显式交过去，否则第一帧（通常是心跳）
     * 会凭空丢掉，界面上表现为"识别到机体了但没有型号"，一直持续到下一帧心跳。
     */
    vehicle.handleFrame(frame);
  }

  private createVehicle(systemId: number, componentId: number): Vehicle {
    const vehicle = new Vehicle({
      systemId,
      componentId,
      feed: this.feed,
      now: this.now,
    });
    this.vehicles.set(vehicle.id, vehicle);

    // 状态事件按"是不是当前操作对象"标注后转发；
    // 主控对象额外走一条 primaryState，免得每个面板各自判断一次"该不该理这辆车"
    this.forwarders.set(vehicle.id, [
      vehicle.events.on('state', (state) => {
        const primary = vehicle.id === this.currentPrimaryId;
        this.events.emit('state', { vehicleId: vehicle.id, vehicle, state, primary });
        if (primary) this.events.emit('primaryState', state);
        // 心跳到达后"这是不是飞控"才算数，所以每次状态变化都重新过一遍自动策略
        this.maybeUpgradePrimary(vehicle);
      }),
    ]);

    return vehicle;
  }

  private maybeUpgradePrimary(candidate: Vehicle): void {
    if (this.disposed || this.primaryExplicit) return;

    const current = this.primary;
    if (current === null) {
      this.switchPrimary(candidate.id, false);
      return;
    }
    if (current === candidate) return;

    /*
     * 只在"当前对象不是飞控、新出现的是飞控"时自动切换：
     * 焦点从伴飞计算机让给飞控是修正，从一架飞机的飞控跳到另一架的飞控是事故。
     */
    if (current.isAutopilot === false && candidate.isAutopilot === true) {
      this.switchPrimary(candidate.id, false);
    }
  }

  private pickFallbackPrimary(): Vehicle | null {
    const all = this.list();
    return all.find((vehicle) => vehicle.isAutopilot === true) ?? all[0] ?? null;
  }

  private switchPrimary(id: string | null, explicit: boolean): void {
    if (id === this.currentPrimaryId) {
      if (explicit && id !== null) this.primaryExplicit = true;
      return;
    }
    this.currentPrimaryId = id;
    // 显式指定后锁定；setPrimary(null) 则交还自动策略
    this.primaryExplicit = explicit && id !== null;
    const vehicle = id === null ? null : this.vehicles.get(id) ?? null;
    this.events.emit('primaryChanged', { vehicleId: id, vehicle, at: this.now() });
  }
}
